import { SEO } from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useEffect } from 'react';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <SEO
        title="关于我们_品牌介绍_联系我们-韩漫网"
        description="了解韩漫网品牌故事和发展历程。韩漫网致力于为您提供高清韩国漫画的免费在线阅读，拥有丰富韩漫大全和易用网址。"
        keywords="关于韩漫网,品牌介绍,联系我们,高清韩国漫画,韩漫网"
        canonicalUrl="https://www.hanmanwang.com/about"
      />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              关于韩漫网
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              韩漫网是一个专注于<strong>高清韩国漫画免费在线阅读</strong>的平台，致力于为广大漫画爱好者提供
              最优质的<strong>韩漫大全</strong>资源和最便捷的<strong>漫画韩漫网址</strong>访问体验。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 mb-4">
                  <i className="ri-lightbulb-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的初心</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  韩漫网创立于2020年，源于创始团队对韩国漫画的热爱。我们发现许多漫画爱好者
                  很难找到高质量、更新及时的<strong>韩漫大全</strong>资源，于是决定打造一个专注韩国漫画的免费阅读平台。
                </p>
              </div>
              <div>
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 mb-4">
                  <i className="ri-star-line text-xl text-accent-500"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为中文世界里最受欢迎的<strong>韩国漫画</strong>阅读平台，让每一位漫画爱好者
                  都能在这里找到自己喜欢的作品，享受极致的<strong>免费韩漫在线阅读</strong>体验。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { num: '500万+', label: '月活跃用户', icon: 'ri-user-line', color: 'primary' },
                { num: '16+', label: '漫画分类', icon: 'ri-layout-grid-line', color: 'accent' },
                { num: '99.7%', label: '用户好评率', icon: 'ri-thumb-up-line', color: 'secondary' },
              ].map((stat) => {
                const colorMap: Record<string, { bg: string; text: string }> = {
                  primary: { bg: 'bg-primary-100', text: 'text-primary-500' },
                  accent: { bg: 'bg-accent-100', text: 'text-accent-500' },
                  secondary: { bg: 'bg-secondary-100', text: 'text-secondary-500' },
                };
                const colors = colorMap[stat.color];
                return (
                  <div key={stat.label} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${colors.bg} mx-auto mb-4`}>
                      <i className={`${stat.icon} text-xl ${colors.text}`}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{stat.num}</p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* User Reviews */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              用户评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '小李', text: '自从发现了韩漫网，我再也不用到处找漫画了。更新快、画质高，强烈推荐！', rating: 5 },
                { name: '漫画迷小王', text: '分类很全面，追的几部韩漫这里都有，而且完全免费，太良心了。', rating: 5 },
                { name: '悠闲读者', text: '每天都在这里看最新的韩国漫画，界面简洁，使用方便，满分好评！', rating: 5 },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <div key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-sm text-primary-500"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.text}</p>
                  <p className="text-xs text-foreground-400 font-medium">—— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', content: 'contact@hanmanwang.com', color: 'primary' },
                { icon: 'ri-phone-line', title: '客服电话', content: '400-888-8888', color: 'accent' },
                { icon: 'ri-map-pin-line', title: '公司地址', content: '广东省广州市天河区体育西路111号', color: 'secondary' },
              ].map((item) => {
                const colorMap: Record<string, { bg: string; text: string }> = {
                  primary: { bg: 'bg-primary-100', text: 'text-primary-500' },
                  accent: { bg: 'bg-accent-100', text: 'text-accent-500' },
                  secondary: { bg: 'bg-secondary-100', text: 'text-secondary-500' },
                };
                const colors = colorMap[item.color];
                return (
                  <div key={item.title} className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${colors.bg} mx-auto mb-3`}>
                      <i className={`${item.icon} text-lg ${colors.text}`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-600">{item.content}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '韩漫网的所有漫画都是免费的吗？', a: '是的！韩漫网上的所有漫画均提供免费在线阅读，无需注册或付费。' },
                { q: '漫画更新频率如何？', a: '我们每日追踪最新韩国漫画更新，确保读者第一时间看到最新章节。' },
                { q: '如何联系我们？', a: '您可以通过邮箱 contact@hanmanwang.com 或客服电话 400-888-8888 联系我们。' },
                { q: '可以在手机上看吗？', a: '当然可以！韩漫网完美适配手机、平板和电脑，随时随地畅享阅读。' },
              ].map((faq, index) => (
                <details key={index} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden group cursor-pointer">
                  <summary className="px-5 py-4 flex items-center justify-between text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">
                    {faq.q}
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://www.hanmanwang.com/" },
              { "@type": "ListItem", position: 2, name: "关于我们" },
            ],
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "韩漫网",
            url: "https://www.hanmanwang.com/",
            description: "韩漫网致力于为您提供高清韩国漫画的免费在线阅读",
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+86-400-888-8888",
              contactType: "customer service",
              availableLanguage: ["Chinese"],
            },
            address: {
              "@type": "PostalAddress",
              streetAddress: "体育西路111号",
              addressLocality: "广州",
              addressRegion: "广东",
              addressCountry: "CN",
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "韩漫网的所有漫画都是免费的吗？", acceptedAnswer: { "@type": "Answer", text: "是的！韩漫网上的所有漫画均提供免费在线阅读，无需注册或付费。" } },
              { "@type": "Question", name: "漫画更新频率如何？", acceptedAnswer: { "@type": "Answer", text: "我们每日追踪最新韩国漫画更新，确保读者第一时间看到最新章节。" } },
              { "@type": "Question", name: "如何联系我们？", acceptedAnswer: { "@type": "Answer", text: "您可以通过邮箱 contact@hanmanwang.com 或客服电话 400-888-8888 联系我们。" } },
              { "@type": "Question", name: "可以在手机上看吗？", acceptedAnswer: { "@type": "Answer", text: "当然可以！韩漫网完美适配手机、平板和电脑，随时随地畅享阅读。" } },
            ],
          }),
        }}
      />
    </>
  );
}